import {
  agentTeams,
  audienceOptions,
  needCategories,
  type ProcurementDraft,
} from "./data";

export type AgentStatus = "Pracuje" | "Pripravený" | "V poradí" | "Čaká na rozhodnutie";

export type AgentAssignment = {
  teamId: string;
  name: string;
  status: AgentStatus;
  task: string;
};

export type CasePlan = {
  id: string;
  createdAt: string;
  segment: string;
  category: string;
  route: string;
  readiness: number;
  riskSignals: string[];
  missingItems: string[];
  nextActions: string[];
  assignments: AgentAssignment[];
};

const requiredFields: Array<keyof ProcurementDraft> = [
  "categoryId",
  "title",
  "description",
  "clientName",
  "contactName",
  "email",
  "deliveryPlace",
  "budgetBand",
  "deadline",
];

function daysUntil(dateValue: string) {
  if (!dateValue) return null;
  const deadline = new Date(`${dateValue}T12:00:00`);
  if (Number.isNaN(deadline.getTime())) return null;
  return Math.ceil((deadline.getTime() - Date.now()) / 86_400_000);
}

export function calculateReadiness(draft: ProcurementDraft) {
  const completed = requiredFields.filter((field) => String(draft[field]).trim().length > 0).length;
  const detailBonus = draft.description.trim().length >= 80 ? 8 : 0;
  const documentBonus = draft.fileNames.length > 0 || draft.hasSpecification ? 8 : 0;
  const priorityBonus = draft.priorities.length > 0 ? 5 : 0;
  return Math.min(100, Math.round((completed / requiredFields.length) * 79) + detailBonus + documentBonus + priorityBonus);
}

export function getMissingItems(draft: ProcurementDraft) {
  const missing: string[] = [];
  if (!draft.categoryId) missing.push("oblasť dopytu");
  if (!draft.title.trim()) missing.push("pracovný názov");
  if (draft.description.trim().length < 40) missing.push("podrobnejší opis potreby");
  if (!draft.clientName.trim()) missing.push(draft.audience === "individual" ? "meno klienta" : "názov organizácie");
  if (!draft.email.trim()) missing.push("kontaktný e-mail");
  if (!draft.deliveryPlace.trim()) missing.push("miesto plnenia");
  if (!draft.budgetBand) missing.push("rozpočtový rámec");
  if (!draft.deadline) missing.push("požadovaný termín");
  if (!draft.hasSpecification && draft.fileNames.length === 0) missing.push("technické alebo obrazové podklady");
  return missing;
}

export function getRiskSignals(draft: ProcurementDraft) {
  const risks: string[] = [];
  const remainingDays = daysUntil(draft.deadline);

  if (draft.audience === "municipality") {
    risks.push("Procesný režim a finančný limit musí potvrdiť kvalifikovaný supervízor pred zverejnením.");
  }
  if (draft.fundingSource.includes("Európske") || draft.fundingSource.includes("dotácia") || draft.fundingSource.includes("Dotácia")) {
    risks.push("Podmienky financovania môžu vyžadovať samostatnú kontrolu oprávnenosti a dokumentácie.");
  }
  if (!draft.hasSpecification) {
    risks.push("Opis predmetu ešte nie je uzamknutý; špecifikačný tím musí odstrániť diskriminačné alebo nevyhodnotiteľné požiadavky.");
  }
  if (draft.budgetBand === "Zatiaľ nevieme") {
    risks.push("Bez rozpočtového rámca sa najprv vykoná trhový benchmark alebo PHZ.");
  }
  if (remainingDays !== null && remainingDays < 30) {
    risks.push("Požadovaný termín je kratší ako 30 dní a môže byť nezlučiteľný s primeranými lehotami alebo kapacitou trhu.");
  }
  if (draft.communicationChannels.includes("Hlasový AI agent")) {
    risks.push("Hlasový agent sa vždy identifikuje ako AI; nahrávanie je vypnuté, kým nie je oznámené a právne povolené.");
  }
  return risks;
}

function assignmentTask(teamId: string, draft: ProcurementDraft) {
  const tasks: Record<string, string> = {
    command: "Rozdeliť prípad, doplniť otázky a nastaviť termíny.",
    law: draft.audience === "municipality"
      ? "Pripraviť návrh režimu ZVO, kontrolných bodov a odbornej eskalácie."
      : "Skontrolovať zmluvné, spotrebiteľské a regulačné riziká podľa segmentu.",
    engineering: "Vytvoriť neutrálnu špecifikáciu a akceptačné kritériá.",
    market: draft.audience === "municipality"
      ? "Pripraviť trhovú mapu a dôkazový podklad pre PHZ."
      : "Vyhľadať vhodných dodávateľov a porovnateľné cenové ponuky.",
    communications: "Pripraviť transparentne označenú písomnú a hlasovú komunikáciu.",
    finance: "Overiť rozpočet, financovanie, TCO a návrh zmluvných podmienok.",
    delivery: "Pripraviť míľniky, prevzatie, kvalitu, fakturáciu a reklamácie.",
    trust: "Vynútiť minimalizáciu údajov, prístupy, auditné logy a AI transparentnosť.",
  };
  return tasks[teamId] ?? "Spracovať pridelenú časť prípadu.";
}

export function createCasePlan(draft: ProcurementDraft, id: string): CasePlan {
  const category = needCategories.find((item) => item.id === draft.categoryId);
  const segment = audienceOptions.find((item) => item.id === draft.audience);
  const route = draft.audience === "municipality"
    ? "Verejný nákup: ZVO + odborná autorizácia"
    : draft.audience === "business"
      ? "Firemný nákup: riadený B2B sourcing"
      : "Marketplace: asistovaný osobný dopyt";

  const assignments = agentTeams.map<AgentAssignment>((team, index) => ({
    teamId: team.id,
    name: team.name,
    status: index < 3 ? "Pracuje" : index === 7 ? "Pripravený" : "V poradí",
    task: assignmentTask(team.id, draft),
  }));

  return {
    id,
    createdAt: new Intl.DateTimeFormat("sk-SK", { dateStyle: "medium", timeStyle: "short" }).format(new Date()),
    segment: segment?.label ?? "Klient",
    category: category?.title ?? "Nezaradená potreba",
    route,
    readiness: calculateReadiness(draft),
    riskSignals: getRiskSignals(draft),
    missingItems: getMissingItems(draft),
    nextActions: [
      "Koordinátor preverí úplnosť a do 15 minút zostaví pracovný plán.",
      draft.audience === "municipality"
        ? "ZVO a trhový tím pripravia návrh režimu a PHZ na odborné potvrdenie."
        : "Trhový tím vytvorí shortlist a porovnávací rámec.",
      "Špecifikačný tím pripraví prvú verziu zadania a otázky pre klienta alebo experta.",
    ],
    assignments,
  };
}
